import { useFormContext } from "react-hook-form";
import {
  FormController,
  FormControllerCheckbox,
  MuiBox,
  MuiCheckbox,
  MuiGrid,
  MuiSelect,
  MuiTextField,
  MuiTypography,
} from "../../../../components";
import { MAX_LENGTH_100, PAGE_IDS } from "../../../../helpers/constants";
import {
  BillingDetailsCheckboxKeys,
  BillingDetailsCheckboxState,
  CreateClientFormKey,
} from "../../modelsAndSchemas/Models";
import { BillingDetailsWrapper } from "./BillingDetails.style";

import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useGetInvoicingLanguages } from "../../../../api/services/client/clientApis";
import { setApiDataToDropDownOption } from "../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { editClientSelector } from "../../../../redux/slices/client/createClientSelector";
import { billingAndPurchaseOrderKeys } from "../../siteCore/SiteCoreKeys";

interface BillingDetailsComponentProps {
  checkboxState: BillingDetailsCheckboxState;
  updateCheckboxState: (key: string) => void;
}

const pageId = PAGE_IDS.BILLING_DETAILS;

export const BillingDetails: React.FC<BillingDetailsComponentProps> = ({
  updateCheckboxState,
  checkboxState,
}) => {
  const { control } = useFormContext();
  const { fetchLabel, commonLabels } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { isEdit: isEditClient } = useSelector(editClientSelector);

  useEffect(() => {}, [checkboxState]);

  const getLabel = (key: keyof typeof billingAndPurchaseOrderKeys) => {
    return fetchLabel(
      billingAndPurchaseOrderKeys.billingDetailsAndPOTitle,
      billingAndPurchaseOrderKeys[key],
    );
  };

  const { data: invoicingLanguages } = useGetInvoicingLanguages();

  const renderCheckbox = (
    name: BillingDetailsCheckboxKeys,
    label: string,
    disabled = false,
  ) => {
    return (
      <FormControllerCheckbox
        key={name}
        control={control}
        name={CreateClientFormKey[name]}
      >
        <MuiCheckbox
          checkboxLabel={label}
          id={`${pageId}-${CreateClientFormKey[name]}`}
          data-testId={`${pageId}-${CreateClientFormKey[name]}`}
          disabled={disabled}
          checked={checkboxState[name]}
          onChange={() => updateCheckboxState(name)}
        />
      </FormControllerCheckbox>
    );
  };

  return (
    <BillingDetailsWrapper>
      <MuiBox className="billing-details-title">
        <MuiTypography
          variant="h2SemiBold"
          data-testid={`${pageId}-${billingAndPurchaseOrderKeys.billingDetailsHeading}`}
        >
          {getLabel("billingDetailsHeading")}
        </MuiTypography>
      </MuiBox>
      <MuiBox>
        <MuiBox className="billing-details-select-applicable">
          <MuiTypography variant="h3">
            {getLabel("selectApplicable")}
          </MuiTypography>
        </MuiBox>
        <MuiBox>
          <MuiGrid className="billing-details-grid">
            {renderCheckbox(
              "consolidatedInvoicing",
              getLabel("consolidatedInvoicing"),
            )}
            {renderCheckbox(
              "activeCompany",
              getLabel("activeCompany"),
              !isEditClient,
            )}
            {renderCheckbox("rebateClient", getLabel("rebateClient"))}
            {renderCheckbox("earlyPayDiscount", getLabel("earlyPayDiscount"))}
          </MuiGrid>
        </MuiBox>
      </MuiBox>

      <MuiBox className="billing-details-early-pay">
        <FormController
          control={control}
          name={CreateClientFormKey.invoiceLanguage}
        >
          <MuiSelect
            label={getLabel("invoiceLanguage")}
            options={setApiDataToDropDownOption(invoicingLanguages)}
            visibleDefaultSelectOption
            id={`${pageId}-${CreateClientFormKey.invoiceLanguage}`}
            value=""
            placeholder={commonLabels.selectPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
            isMandatory
          />
        </FormController>
        {checkboxState.earlyPayDiscount && (
          <>
            <FormController
              control={control}
              name={CreateClientFormKey.earlyPayDays}
            >
              <MuiTextField
                isMandatory
                id={`${pageId}-${CreateClientFormKey.earlyPayDays}`}
                label={getLabel("earlyPayDays")}
                placeholder={commonLabels.inputPlaceHolder}
                type="number"
                maxLength={MAX_LENGTH_100}
                data-testId={`${pageId}-${CreateClientFormKey.earlyPayDays}`}
                inputNumberWithoutDecimal
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>

            <FormController
              control={control}
              name={CreateClientFormKey.earlyPayPercentage}
            >
              <MuiTextField
                isMandatory
                id={`${pageId}-${CreateClientFormKey.earlyPayPercentage}`}
                label={getLabel("earlyPayPercentage")}
                placeholder={commonLabels.inputPlaceHolder}
                type="number"
                maxLength={MAX_LENGTH_100}
                infoMessage={getLabel("earlyPayTooltipMessage")}
                data-testId={`${pageId}-${CreateClientFormKey.earlyPayPercentage}`}
                inputNumberWithoutDecimal
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </>
        )}
      </MuiBox>

      <MuiBox className="billing-details-notes">
        <FormController control={control} name={CreateClientFormKey.notes}>
          <MuiTextField
            id={`${pageId}-${CreateClientFormKey.notes}`}
            label={commonLabels.notes}
            placeholder={commonLabels.inputPlaceHolder}
            className="notes-input"
            multiline
            rows={4}
            data-testId={`${pageId}-${CreateClientFormKey.notes}`}
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiBox>
    </BillingDetailsWrapper>
  );
};
